export type CardRequestType = 'virtual_card' | 'physical_card' | 'replacement_card';

export type CardRequestStatus = 'open' | 'approved' | 'rejected' | 'expired' | 'failed';

export type CardRequestCost = {
  one_off_cents: number;
  recurring_monthly_cents: number;
};

export type CardApprovalRequest = {
  id: string;
  card_kind: CardRequestType;
  employee_id: string;
  first_name: string;
  last_name: string;
  requested_at: string;
  /** Null for requests opened before the deadline was written. */
  expires_on: string | null;
  requires_swan_consent: boolean;
  cost: CardRequestCost;
  status: CardRequestStatus;
  /** Why issuing failed, as a code the client turns into a sentence. */
  failure_code: string;
  /** Swan's own words, when the failure was Swan's. Not translatable by us. */
  failure_reason: string | null;
  /** The cards are at Swan and the consent is unsigned. Deciding again reopens
   * that signature instead of issuing a second card. */
  awaiting_signature: boolean;
};

/**
 * Whether the signed-in manager may approve at all: approving grants the
 * consent Swan asks for, which needs the account permission plus a completed
 * identification.
 */
export type CardApprover = {
  has_swan_permission: boolean;
  identification_completed: boolean;
};

export type CardApprovalsResponse = {
  results: CardApprovalRequest[];
  approver: CardApprover;
};

export type CardApprovalDecision = 'approve' | 'reject' | 'retry';

export type CardApprovalDecisionResponse = {
  results: CardApprovalRequest[];
  /** Where the manager signs — for what this call issued, or for a consent an
   * earlier attempt left unsigned. Empty when there is nothing to sign. */
  consent_url: string;
};

/** Where one person stands, derived across activation, request and card. */
export type CardPersonStatus =
  | 'not_activated'
  | 'awaiting_approval'
  | 'issuing_failed'
  | 'rejected'
  | 'request_expired'
  | 'card_pending'
  | 'active'
  | 'blocked'
  | 'grace_period'
  | 'ended';

/** Why an activation has not produced a request yet. */
export type CardNotActivatedReason =
  'never_started' | 'aborted' | 'sign_up_failed' | 'in_progress' | '';

export type CardKind = 'virtual' | 'physical';

export type CardStatusCard = {
  kind: CardKind;
  last_four_digits: string;
  wallet_providers: string[];
};

export type CardStatusPaymentRegion = {
  postal_code: string;
  city: string;
  region_code: string;
  neighbor_count: number;
};

export type CardStatusRow = {
  employee_id: string;
  first_name: string;
  last_name: string;
  exit_date: string | null;
  status: CardPersonStatus;
  /** The last day the card pays; only set while the status is `grace_period`. */
  grace_period_ends_at: string | null;
  not_activated_reason: CardNotActivatedReason;
  benefit_set_name: string;
  cards: CardStatusCard[];
  payment_region: CardStatusPaymentRegion | null;
  /** The ceiling set at issuing, not a balance. */
  monthly_limit_cents: number;
};

export type CardOverviewResponse = {
  results: CardStatusRow[];
};

export type CardOnboardingPhase = 'not_started' | 'in_progress' | 'verified' | 'refused';

export type CardOnboardingStepId =
  | 'start'
  | 'swan_form'
  | 'identification'
  | 'documents'
  | 'tax_id'
  | 'first_transfer'
  | 'ubo_details'
  | 'legal_representative_details'
  | 'organization_details'
  | 'other'
  | 'review';

export type CardOnboardingStepState = 'done' | 'todo' | 'waiting' | 'upcoming';

export type CardOnboardingTaxSubject = {
  id: string;
  kind: 'company' | 'ubo';
  name: string;
  /**
   * Swan takes a beneficial owner's tax number only through the onboarding,
   * which is finalized before it asks, so those rows carry no field.
   */
  editable: boolean;
  due_on: string;
  days_left: number;
};

export type CardOnboardingStep = {
  id: CardOnboardingStepId;
  state: CardOnboardingStepState;
  payload: {
    onboarding_url?: string;
    link_unavailable?: boolean;
    started_at?: string | null;
    representation?: 'self' | 'other' | '';
    /** Swan's `SwanIdentificationStatus`, lowercased. */
    identification_status?: string;
    name?: string;
    approver_linked?: boolean;
    completed?: boolean;
    collection_id?: string;
    review_requested?: boolean;
    subjects?: CardOnboardingTaxSubject[];
    iban?: string;
  };
};

/** Swan names the concrete requirement only while it waits for information. */
export type PendingRequirement =
  | 'supporting_documents_required'
  | 'ubo_details_required'
  | 'tax_id_required'
  | 'legal_representative_details_required'
  | 'organization_details_required'
  | 'first_transfer_required'
  | 'other';

export type MonthlyPaymentVolume =
  'less_than_10000' | 'between_10000_and_50000' | 'between_50000_and_100000' | 'more_than_100000';

export type CardOnboardingCompany = {
  name?: string;
  /** Prefill only — the contact on file may not be the manager signing in. */
  contact_first_name?: string;
  contact_last_name?: string;
  iban?: string;
  legal_representative_name?: string;
  monthly_payment_volume?: MonthlyPaymentVolume | '';
};

export type CardOnboardingResponse = {
  phase: CardOnboardingPhase;
  /** Swan's onboarding axis: '', 'invalid', 'valid' or 'finalized'. */
  onboarding_status: string;
  verification_status: string;
  action_required_by_company: boolean;
  pending_requirement: PendingRequirement | null;
  verified_at: string | null;
  refused_reason: string;
  steps: CardOnboardingStep[];
  company: CardOnboardingCompany;
};

/** Mirrors Swan's `SupportingDocumentStatus`. */
export type CardOnboardingDocumentStatus =
  'waitingforupload' | 'uploaded' | 'validated' | 'refused' | 'notuploaded';

export type CardOnboardingDocument = {
  name: string;
  acceptable_types: string[];
  status: CardOnboardingDocumentStatus;
  uploaded: boolean;
  refused_reason: string;
};

/** Mirrors Swan's `SupportingDocumentCollectionStatus`. */
export type CardOnboardingDocumentCollectionStatus =
  'waitingfordocument' | 'pendingreview' | 'approved' | 'rejected' | 'canceled';

export type CardOnboardingDocumentCollection = {
  status: CardOnboardingDocumentCollectionStatus | '';
  /** Swan refuses every upload outside `waitingfordocument`. */
  accepts_uploads: boolean;
  documents: CardOnboardingDocument[];
};

export type CardOnboardingRepresentation = 'self' | 'other';

export type CardOnboardingAccountAdmin = {
  first_name: string;
  last_name: string;
  /** Only when someone else represents: signing in supplies the manager's. */
  email?: string;
};

/** What a benefit set grants on the card. `card_enabled` is false for a set
 * without an offer. */
export type CardOffer = {
  card_enabled: boolean;
  physical_card_enabled: boolean;
};

export const NO_CARD_OFFER: CardOffer = { card_enabled: false, physical_card_enabled: false };
export type WalletPageState =
  'empty' | 'funded' | 'covered' | 'shortfall' | 'escalation_week' | 'escalation_last_day';

export type WalletTransferDetails = {
  recipient: string;
  iban: string;
  bic: string;
  reference: string;
};

export type WalletSummary = {
  balance_cents: number;
  /** Null before the first transfer arrived. */
  balance_as_of: string | null;
  transfer_details: WalletTransferDetails;
  current_month: { shortfall_cents: number; deadline: string | null };
};

export type WalletPositionKind = 'monthly_benefit' | 'bonus';
export type WalletHighlight = 'red' | 'green' | 'none';

export type WalletPosition = {
  key: string;
  kind: WalletPositionKind;
  /** "Sachbezug" for monthly positions; the client appends the month name. */
  label: string;
  due_month: string;
  due_date: string;
  catch_up_until: string;
  amount_cents: number;
  recipient_count: number;
  tax_treatment: string;
  coverage_cents: number;
  is_first_uncovered: boolean;
  is_covered: boolean;
  highlight: WalletHighlight;
};

/**
 * Open positions before a custom „Von“ that lies after the current month. The
 * payload's totals already include them.
 */
export type WalletEarlierPositions = {
  until: string;
  amount_cents: number;
  coverage_cents: number;
  positions_count: number;
  recipients_total: number;
  highlight: WalletHighlight;
};

export type WalletUpcomingPayments = {
  from: string;
  until: string;
  total_cents: number;
  positions_count: number;
  recipients_total: number;
  shortfall_cents: number;
  shortfall_current_month_cents: number;
  deadline: string | null;
  state: WalletPageState;
  /** Whole Berlin days to the deadline, 0 on the last day; null outside the two escalation states. */
  escalation_days_left: number | null;
  /** The month the escalation is about (`YYYY-MM`); null outside the two escalation states. */
  escalation_month: string | null;
  earlier?: WalletEarlierPositions | null;
  results: WalletPosition[];
};

export type WalletTransactionKind = 'top_up' | 'funding' | 'other';

export type WalletTransaction = {
  id: string;
  kind: WalletTransactionKind;
  booked_at: string;
  amount_cents: number;
  balance_after_cents: number;
  label: string;
  source: 'monthly_benefit' | 'bonus' | null;
  accounting_period: string | null;
  recipient_count: number | null;
  tax_treatment: string | null;
};

export type WalletTransactions = {
  year: number;
  has_previous_year: boolean;
  closing_balance_cents: number;
  results: WalletTransaction[];
};
